import { Button, Icon, MuiAlert, ResponsiveDrawer, Select, strIsNullOrEmpty, TextField } from '@fox/atoms';
import { QueryFilter, SortState } from '@fox/data-grid';
import { FlexRowSpaced } from '@fox/layout';
import { useMediaQuery } from '@mui/material';
import React, { useState } from 'react';
import { CSVLink } from 'react-csv';
import styled from 'styled-components';
import { sanitiseFileName } from '../../../common/helper/helper';
import { useContentContext } from '../../content-context';
import { renderDate } from '../financial-details/shared';
import { createActionCentrePDF, fetchActionCentreData } from './action-centre-data';
import { ActionCentreColumn, FILTER_MIN_WIDTH, SwiftFoxActionCentreColumnsResult } from './contracts';

export function SearchInput({ updateFilters }: { updateFilters: (value: string | undefined, column?: ActionCentreColumn) => void }) {
	return (
		<TextField
			onChange={e => {
				//for the global search we create this 'column' to identify the search filter on the backend
				const searchColumn = {
					filterType: 'string',
					id: 'search',
					title: 'search',
					type: 'search'
				};
				if (!strIsNullOrEmpty(e.target.value)) {
					updateFilters(e.target.value, searchColumn);
				} else {
					updateFilters(undefined, searchColumn);
				}
			}}
			size="small"
			placeholder="Search"
			endAdornment={<Icon iconName="Search" />}
		/>
	);
}

export function ClearFiltersButton({
	enableClearFilters,
	setFilters,
	setPage,
	filters
}: {
	filters: QueryFilter[];
	enableClearFilters: boolean;
	setFilters: React.Dispatch<React.SetStateAction<QueryFilter[]>>;
	setPage: React.Dispatch<React.SetStateAction<number>>;
}) {
	return (
		<Button
			variant="tertiary"
			disabled={!enableClearFilters}
			onClick={() => {
				setFilters([]);
				setPage(1);
			}}
		>
			{`Clear filters${filters?.length ? ` (${filters.length})` : ''}`}
		</Button>
	);
}

export function SourceFilter({
	sourceOptions,
	filter,
	setSourceFilter
}: {
	sourceOptions: [string, string][];
	filter: string;
	setSourceFilter: (value: string) => void;
}) {
	return (
		<Select<[string, string], false, false, false>
			getOptionLabel={([key, value]) => value}
			options={sourceOptions}
			value={sourceOptions.find(([key, value]) => key == filter?.toString())}
			onChange={(e, [key, _]) => setSourceFilter(key)}
			size="small"
			sx={{ minWidth: FILTER_MIN_WIDTH }}
			disableClearable={false}
		/>
	);
}
export function ExportButtons({
	actionId,
	columnsResult,
	filters,
	sourceOptions,
	filter,
	sort
}: {
	actionId: string;
	columnsResult: SwiftFoxActionCentreColumnsResult;
	sourceOptions: [string, string][];
	filters: QueryFilter[];
	sort?: SortState;
	filter?: string;
}) {
	return (
		<FlexRowSpaced>
			<ExportButton
				actionId={actionId}
				actionName={columnsResult?.name}
				columns={columnsResult.columns}
				source={sourceOptions.find(([key, value]) => key == filter?.toString())}
				filters={filters}
				sort={sort}
			/>
			<PdfButton actionId={actionId} sourceFilter={filter} filters={filters} sort={sort} />
		</FlexRowSpaced>
	);
}

export function PdfButton({
	actionId,
	sourceFilter,
	filters,
	sort
}: {
	actionId: string;
	filters: QueryFilter[];
	sourceFilter?: string;
	sort?: SortState;
}) {
	const { contentId, requestToken } = useContentContext();
	const [loading, setLoading] = useState<boolean>(false);
	const [pdf, setPdf] = useState<string | undefined>(undefined);
	const [error, setError] = useState<string>('');

	const isMobile = useMediaQuery('(max-width: 800px)');

	if (error) return <MuiAlert severity="error">{error}</MuiAlert>;

	return (
		<>
			<Button
				disabled={loading}
				startIconName="FilePdf1"
				onClick={async () => {
					setLoading(true);
					setError('');
					const res = await createActionCentrePDF({ contentId, id: actionId, filters, sourceFilter, sort }, requestToken);
					if (res.error) {
						setError(res.error);
					} else {
						setPdf(res.pdfString);
					}
					setLoading(false);
				}}
				variant="secondary"
			>
				Create PDF
			</Button>

			<ResponsiveDrawer
				headerVariant="h5"
				height={isMobile ? '100%' : undefined}
				style={isMobile ? { height: '100%' } : {}}
				placement={isMobile ? 'bottom' : 'right'}
				open={!strIsNullOrEmpty(pdf)}
				width={isMobile ? '100%' : 800}
				title="Export to PDF"
				onClose={() => setPdf(undefined)}
			>
				<>{pdf ? <iframe title="Export to PDF" width="100%" height="100%" src={pdf}></iframe> : <></>}</>
			</ResponsiveDrawer>
		</>
	);
}

function formatDataAndHeaders(
	columns: ActionCentreColumn[],
	data: Record<string, string>[],
	name: string
): { formattedData: Record<string, string>[]; headers: { key: string; label: string }[] } {
	let formattedData = [
		{ c0: name ?? '' },
		{},
		Object.entries(data?.[0] ?? {}).reduce<Record<string, string>>((prev, [key, value], i) => {
			const columnName = columns.find(c => c.id === key)?.title ?? '';
			prev[`c${i}`] = columnName;
			return prev;
		}, {})
	];
	formattedData = [
		...formattedData,
		...data.map((record: Record<string, string>, i) => {
			return Object.entries(record).reduce<Record<string, string>>((prev, [key, value], i) => {
				prev[`c${i}`] = value;
				return prev;
			}, {});
		})
	];

	const headers = columns.map((c, i) => ({ key: `c${i}`, label: '' }));

	return { formattedData, headers };
}

export function ExportButton({
	actionId,
	source,
	columns,
	actionName,
	filters,
	sort
}: {
	actionId: string;
	columns: ActionCentreColumn[];
	actionName: string;
	filters?: QueryFilter[];
	source?: [string, string];
	sort?: SortState | undefined;
}) {
	const { contentId, requestToken } = useContentContext();
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string>('');
	const [data, setData] = useState<Record<string, string>[] | undefined>(undefined);
	const [headers, setHeaders] = useState<{ key: string; label: string }[] | undefined>(undefined);
	const [actionCentreName, setActionCentreName] = useState<string | undefined>(undefined);

	const [sourceFilter, sourceName] = source ?? [];

	if (error) return <MuiAlert severity="error">{error}</MuiAlert>;

	if (!data) {
		return (
			<Button
				startIconName="Download"
				disabled={loading}
				onClick={async () => {
					setLoading(true);
					setError('');
					const res = await fetchActionCentreData({ contentId, id: actionId, filters, sourceFilter, sort }, requestToken);
					if (res.error) {
						setError(res.error);
					} else {
						const name = `${actionName}${sourceName ? ` - ${sourceName}` : ''} - ${renderDate(new Date())}`;
						const { formattedData, headers } = res.result?.data ? formatDataAndHeaders(columns, res.result.data, name) : {};
						setData(formattedData);
						setHeaders(headers);
						setActionCentreName(sanitiseFileName(name));
					}
					setLoading(false);
				}}
				variant="secondary"
			>
				Export
			</Button>
		);
	}

	return (
		<FlexRowSpaced>
			<Button variant="tertiary" onClick={() => setData(undefined)}>
				Cancel
			</Button>
			<ButtonLinkWrapper>
				<CSVLink
					data={data ?? []}
					headers={headers}
					filename={actionCentreName ? `${actionCentreName}.csv` : `export_${renderDate(new Date())}.csv`}
					enclosingCharacter={`"`}
				>
					<Button
						sx={{ height: '100%' }}
						startIconName="Download"
						variant="secondary"
						title="Download File"
						onClick={() => setData(undefined)}
					>
						Download Export
					</Button>
				</CSVLink>
			</ButtonLinkWrapper>
		</FlexRowSpaced>
	);
}

export const ButtonLinkWrapper = styled.div`
	a {
		color: inherit;
		text-decoration: inherit;
	}
`;

export const TableAndFilterWrapper = styled.div`
	margin-left: 1rem;
	display: flex;
	gap: 1.5rem;
`;
